<template>
  <div class="fin-dash">
    <header class="fin-dash__head">
      <div class="fin-dash__head-text">
        <h1 class="fin-dash__title">경영 손익 요약</h1>
        <!-- <p class="fin-dash__sub">
          CFO 관점 월별 추이·판관비 구성·손익 요약을 한 화면에서 봅니다.
        </p> -->
      </div>
      <div class="fin-dash__head-actions">
        <el-date-picker
          v-model="periodMonth"
          type="month"
          size="small"
          placeholder="기준 월"
          value-format="yyyy-MM"
          format="yyyy년 M월"
          class="fin-dash__month"
          @change="onPeriodChange"
        />
        <el-tooltip content="무작위로 수치를 다시 맞춥니다" placement="bottom">
          <el-button
            type="default"
            size="small"
            icon="el-icon-refresh"
            circle
            class="fin-dash__refresh"
            @click="onRefresh"
          />
        </el-tooltip>
      </div>
    </header>

    <section class="fin-dash__kpis">
      <el-card
        v-for="item in kpis"
        :key="item.key"
        class="fin-dash__kpi-card"
        shadow="hover"
      >
        <p class="fin-dash__kpi-label">{{ item.label }}</p>
        <p class="fin-dash__kpi-value">{{ item.value }}</p>
        <p class="fin-dash__kpi-meta">{{ item.meta }}</p>
      </el-card>
    </section>

    <el-row :gutter="20" class="fin-dash__row">
      <el-col :xs="24" :lg="16">
        <el-card class="fin-dash__chart-card" shadow="never">
          <div slot="header" class="fin-dash__card-head">
            <span>월별 매출·영업이익 추이 (최근 12개월)</span>
          </div>
          <apexchart
            :key="'fin-line-' + chartRevision"
            type="area"
            height="320"
            :options="trendOptions"
            :series="trendSeries"
          />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card class="fin-dash__chart-card" shadow="never">
          <div slot="header" class="fin-dash__card-head">
            <span>판관비 구성 (당월)</span>
          </div>
          <apexchart
            :key="'fin-donut-' + chartRevision"
            type="donut"
            height="320"
            :options="costDonutOptions"
            :series="costDonutSeries"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="fin-dash__row">
      <el-col :xs="24">
        <el-card class="fin-dash__chart-card" shadow="never">
          <div slot="header" class="fin-dash__card-head">
            <span>손익 요약 (당월 vs 전월)</span>
          </div>
          <el-table :data="plRows" stripe size="small" class="fin-dash__table">
            <el-table-column
              prop="line"
              label="구분"
              min-width="160"
              sortable
            />
            <el-table-column
              prop="current"
              label="당월"
              width="148"
              align="right"
              sortable
              :sort-method="sortPlByCurrent"
            />
            <el-table-column
              prop="prev"
              label="전월"
              width="148"
              align="right"
              sortable
              :sort-method="sortPlByPrev"
            />
            <el-table-column
              prop="chg"
              label="증감"
              width="128"
              align="right"
              sortable
              :sort-method="sortPlByChg"
            >
              <template slot-scope="{ row }">
                <span
                  :class="[
                    'fin-dash__chg',
                    row.chgUp === true
                      ? 'is-up'
                      : row.chgUp === false
                      ? 'is-down'
                      : '',
                  ]"
                  >{{ row.chg }}</span
                >
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
const PRIMARY = "#254aa5";
const PRIMARY_LIGHT = "#4a90e2";
const MUTED = "#8b95a5";

const MONTH_SHORT = [
  "1월",
  "2월",
  "3월",
  "4월",
  "5월",
  "6월",
  "7월",
  "8월",
  "9월",
  "10월",
  "11월",
  "12월",
];

function buildTrend(seed) {
  const rev = MONTH_SHORT.map((_, i) => {
    const base = 9.2 + (seed % 7) / 10;
    return Number(
      (
        base +
        (i / 11) * 2.4 +
        Math.sin((i / 12) * Math.PI * 2) * 0.85 +
        ((i * 5 + seed) % 13) / 40
      ).toFixed(2)
    );
  });
  const op = rev.map((r, i) => {
    const v = Number(
      (r * (0.08 + (seed % 5) / 100) - 0.15 + ((i + seed) % 7) / 50).toFixed(2)
    );
    return Math.max(0.08, v);
  });
  return { labels: MONTH_SHORT, rev, op };
}

function buildCostSplit(seed) {
  const a = 32 + (seed % 9);
  const b = 24 + (seed % 7);
  const c = 18 + (seed % 6);
  const d = Math.max(8, 100 - a - b - c);
  return [a, b, c, d];
}

export default {
  name: "FinanceCfoDashboard",
  data() {
    const seed = 61;
    const trend = buildTrend(seed);
    const cost = buildCostSplit(seed);
    return {
      chartRevision: 0,
      periodMonth: "2026-04",
      kpis: [
        {
          key: "rev",
          label: "매출액 (당월)",
          value: "₩ 12.8억",
          meta: "전월 대비 +6.1%",
        },
        {
          key: "gp",
          label: "매출총이익",
          value: "₩ 4.3억",
          meta: "매출총이익률 33.6%",
        },
        {
          key: "op",
          label: "영업이익",
          value: "₩ 1.05억",
          meta: "영업이익률 8.2%",
        },
        {
          key: "net",
          label: "당기순이익",
          value: "₩ 0.78억",
          meta: "순이익률 6.1%",
        },
      ],
      trendSeries: [
        { name: "매출 (억원)", data: trend.rev },
        { name: "영업이익 (억원)", data: trend.op },
      ],
      trendOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        colors: [PRIMARY, "#67c23a"],
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.04,
            stops: [0, 92, 100],
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: trend.labels,
          labels: { style: { colors: MUTED, fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            style: { colors: MUTED },
            formatter: (v) => `${v}`,
          },
        },
        grid: { borderColor: "#eef0f3", strokeDashArray: 4 },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",
          labels: { colors: "#505258" },
        },
        tooltip: {
          theme: "light",
          fillSeriesColor: false,
        },
      },
      costDonutSeries: cost,
      costDonutOptions: {
        chart: { fontFamily: "Pretendard, sans-serif" },
        labels: ["인건비", "마케팅", "물류·운임", "기타 판관"],
        colors: [PRIMARY, PRIMARY_LIGHT, "#67c23a", "#e6a23c"],
        legend: {
          position: "bottom",
          fontSize: "12px",
          labels: { colors: "#505258" },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                name: { show: true },
                value: {
                  show: true,
                  formatter: (val) =>
                    val != null && val !== "" ? `${val}%` : "",
                },
                total: { show: false },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        tooltip: {
          theme: "light",
          fillSeriesColor: false,
          y: { formatter: (v) => `${v}%` },
        },
      },
      plRows: [
        {
          line: "매출액",
          current: "12.80억",
          prev: "12.06억",
          chg: "+6.1%",
          chgUp: true,
        },
        {
          line: "매출원가",
          current: "8.50억",
          prev: "8.12억",
          chg: "+4.7%",
          chgUp: false,
        },
        {
          line: "매출총이익",
          current: "4.30억",
          prev: "3.94억",
          chg: "+9.1%",
          chgUp: true,
        },
        {
          line: "판매비와관리비",
          current: "3.25억",
          prev: "3.18억",
          chg: "+2.2%",
          chgUp: false,
        },
        {
          line: "영업이익",
          current: "1.05억",
          prev: "0.76억",
          chg: "+38.2%",
          chgUp: true,
        },
        {
          line: "영업외손익",
          current: "−0.05억",
          prev: "0.02억",
          chg: "—",
          chgUp: null,
        },
        {
          line: "당기순이익",
          current: "0.78억",
          prev: "0.58억",
          chg: "+34.5%",
          chgUp: true,
        },
      ],
    };
  },
  methods: {
    parsePlEok(val) {
      if (val == null || val === "") {
        return 0;
      }
      const s = String(val)
        .replace(/\u2212/g, "-")
        .replace("억", "")
        .trim();
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : 0;
    },
    sortPlByCurrent(a, b) {
      return this.parsePlEok(a.current) - this.parsePlEok(b.current);
    },
    sortPlByPrev(a, b) {
      return this.parsePlEok(a.prev) - this.parsePlEok(b.prev);
    },
    sortPlByChg(a, b) {
      const pct = (row) => {
        const s = row.chg;
        if (s == null || s === "—" || s === "") {
          return null;
        }
        const n = parseFloat(String(s).replace(/%/g, ""));
        return Number.isFinite(n) ? n : null;
      };
      const va = pct(a);
      const vb = pct(b);
      if (va === null && vb === null) {
        return 0;
      }
      if (va === null) {
        return 1;
      }
      if (vb === null) {
        return -1;
      }
      return va - vb;
    },
    onPeriodChange() {
      this.regenerateFromSeed(
        this.hashPeriod(this.periodMonth) + (Date.now() % 997)
      );
    },
    onRefresh() {
      this.regenerateFromSeed(Date.now() % 10000);
      this.$message.success("손익 데이터를 갱신했습니다.");
    },
    hashPeriod(ym) {
      if (!ym || typeof ym !== "string") return 0;
      return ym.split("-").reduce((a, x) => a + parseInt(x, 10), 0);
    },
    regenerateFromSeed(seed) {
      const t = buildTrend(seed);
      const cost = buildCostSplit(seed);
      this.trendSeries = [
        { name: "매출 (억원)", data: t.rev },
        { name: "영업이익 (억원)", data: t.op },
      ];
      this.trendOptions = {
        ...this.trendOptions,
        xaxis: { ...this.trendOptions.xaxis, categories: t.labels },
      };
      this.costDonutSeries = cost;
      const revLast = t.rev[t.rev.length - 1];
      const revPrev = t.rev[t.rev.length - 2] || revLast * 0.94;
      const cogs = revLast * (0.64 + (seed % 5) / 200);
      const gp = revLast - cogs;
      const opex = revLast * (0.24 + (seed % 4) / 100);
      const op = gp - opex;
      const net = op * (0.72 + (seed % 8) / 100);
      const fmt = (n) => `${n.toFixed(2)}억`;
      const pct = (a, b) =>
        b === 0
          ? "0%"
          : `${((a - b) / b) * 100 >= 0 ? "+" : ""}${(
              ((a - b) / b) *
              100
            ).toFixed(1)}%`;
      this.kpis = [
        {
          key: "rev",
          label: "매출액 (당월)",
          value: `₩ ${fmt(revLast)}`,
          meta: `전월 대비 ${pct(revLast, revPrev)}`,
        },
        {
          key: "gp",
          label: "매출총이익",
          value: `₩ ${fmt(gp)}`,
          meta: `매출총이익률 ${((gp / revLast) * 100).toFixed(1)}%`,
        },
        {
          key: "op",
          label: "영업이익",
          value: `₩ ${fmt(op)}`,
          meta: `영업이익률 ${((op / revLast) * 100).toFixed(1)}%`,
        },
        {
          key: "net",
          label: "당기순이익",
          value: `₩ ${fmt(net)}`,
          meta: `순이익률 ${((net / revLast) * 100).toFixed(1)}%`,
        },
      ];
      this.plRows = [
        {
          line: "매출액",
          current: fmt(revLast),
          prev: fmt(revPrev),
          chg: pct(revLast, revPrev),
          chgUp: revLast >= revPrev,
        },
        {
          line: "매출원가",
          current: fmt(cogs),
          prev: fmt(cogs * 0.97),
          chg: pct(cogs, cogs * 0.97),
          chgUp: false,
        },
        {
          line: "매출총이익",
          current: fmt(gp),
          prev: fmt(gp * 0.92),
          chg: pct(gp, gp * 0.92),
          chgUp: true,
        },
        {
          line: "판매비와관리비",
          current: fmt(opex),
          prev: fmt(opex * 0.98),
          chg: pct(opex, opex * 0.98),
          chgUp: false,
        },
        {
          line: "영업이익",
          current: fmt(op),
          prev: fmt(op * 0.85),
          chg: pct(op, op * 0.85),
          chgUp: true,
        },
        {
          line: "영업외손익",
          current: "−0.05억",
          prev: "0.02억",
          chg: "—",
          chgUp: null,
        },
        {
          line: "당기순이익",
          current: fmt(net),
          prev: fmt(net * 0.88),
          chg: pct(net, net * 0.88),
          chgUp: true,
        },
      ];
      this.chartRevision += 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.fin-dash {
  width: 100%;
  padding: 24px 32px 48px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f4f6f8 0%, #fff 28%);
}

.fin-dash__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ebeef5;
}

.fin-dash__head-text {
  min-width: 0;
}

.fin-dash__title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.03em;
}

.fin-dash__sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #909399;
}

.fin-dash__head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.fin-dash__month {
  width: 168px;
}

.fin-dash__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}

@media (max-width: 992px) {
  .fin-dash__kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .fin-dash__kpis {
    grid-template-columns: 1fr;
  }
}

.fin-dash__kpi-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.fin-dash__kpi-label {
  margin: 0 0 6px;
  font-size: 12px;
  color: #909399;
}

.fin-dash__kpi-value {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.02em;
}

.fin-dash__kpi-meta {
  margin: 0;
  font-size: 12px;
  color: #606266;
}

.fin-dash__row {
  margin-bottom: 20px !important;
}

@media (max-width: 1199px) {
  .fin-dash__row > .el-col:not(:last-child) {
    margin-bottom: 20px;
  }
}

.fin-dash__chart-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
  margin-bottom: 0;
}

.fin-dash__card-head {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.fin-dash__table {
  width: 100%;
}

.fin-dash__chg.is-up {
  color: #67c23a;
  font-weight: 600;
}

.fin-dash__chg.is-down {
  color: #e6a23c;
  font-weight: 600;
}
</style>
